import Axios from "axios"
import React, { useRef, useState } from "react"
import { csSkillsSet, itSkillsSet, softSkillsSet } from "../Utils"
import { Config } from "./config_section"
import { AnalyzeDescriptionModal } from "./AnalyzeModals/AnalyzeDescriptionModal"
import { createSkillId } from "./utils"
import { Button, Dropdown } from "antd"
import {
	PlusOutlined,
	FormOutlined,
	AlignLeftOutlined,
	FilePdfOutlined,
	LinkOutlined,
	SearchOutlined,
} from "@ant-design/icons"
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf"
import { AnalyzeUrlModal } from "./AnalyzeModals/AnalyzeUrlModal"

pdfjsLib.GlobalWorkerOptions.workerSrc = Config.server.pdfjsWorker

async function extractTextFromPDF( file ) {
	const arrayBuffer = await file.arrayBuffer()
	const pdf = await pdfjsLib.getDocument( { data: arrayBuffer } ).promise

	let fullText = ""
	for ( let i = 1; i <= pdf.numPages; i++ ) {
		const page = await pdf.getPage( i )
		const content = await page.getTextContent()
		fullText += content.items.map( i => i.str ).join( " " )
	}
	return fullText
}

const menuItems = [
	{ icon: <FormOutlined />, key: "manual", label: "Create Manually" },
	{ icon: <AlignLeftOutlined />, key: "description", label: "Analyze Description" },
	{ icon: <FilePdfOutlined />, key: "pdf", label: "Analyze PDF" },
	{ icon: <LinkOutlined />, key: "url", label: "Analyze URL" },
	{ icon: <SearchOutlined />, key: "pick", label: "Pick Trainings" },
]

export const AddSkillButton = ( {
	showMessage,
	onAnalyzingStart,
	openAddForm,
	form,
	analyzing,
	onPickTrainings,
	onAnalyzingEnd,
} ) => {
	const [ openedModal, setOpenedModal ] = useState( null )
	const analyzingRef = React.useRef( analyzing )
	const uploadPdfInputRef = useRef( null )

	React.useEffect( () => {
		analyzingRef.current = analyzing
	}, [ analyzing ] )

	const handleAnalyze = async ( skillType, { file, text, url } ) => {
		const formattedAnalysisType = skillType === "pdf"
			? "PDF file"
			: skillType === "url"
			? "URL"
			: skillType === "description"
			? "Description"
			: "Unknown Type"

		showMessage( { type: "loading", content: `Analyzing ${formattedAnalysisType}...`, key: "updatable", duration: 5 } )
		onAnalyzingStart()
		setOpenedModal( null )
		openAddForm()

		form.setFieldValue( "name", `My Skill (${formattedAnalysisType} Analysis)` )
		try {
			let pdfText
			let response
			switch ( skillType ) {
				case "pdf":
					pdfText = await extractTextFromPDF( file )
					response = await Axios.post(
						Config.server.analyzeCourseDescription,
						{ description: pdfText },
						{ headers: { "Content-Type": "application/json" } },
					)
					break
				case "description":
					response = await Axios.post(
						Config.server.analyzeCourseDescription,
						{ description: text },
						{ headers: { "Content-Type": "application/json" } },
					)
					break
				case "url":
					response = await Axios
						.post(
							Config.server.analyzeUrl,
							{ url },
							{ headers: { "Content-Type": "application/json" } },
						)
					break
				default:
					response = { data: { error: true } }
			}
			if ( !analyzingRef.current ) {
				return
			}

			showMessage( { type: "loading", content: `Analyzing ${formattedAnalysisType}...`, key: "updatable", duration: 0.2 } )

			if ( response && response.data.error === false ) {
				form.setFieldValue( "id", createSkillId() )
				form.setFieldValue( "itSkillsGroup", response.data.data.prediction.skills_groups.filter( ( e ) => itSkillsSet.includes( e ) ) )
				form.setFieldValue( "csSkillsGroup", response.data.data.prediction.skills_groups.filter( ( e ) => csSkillsSet.includes( e ) ) )
				form.setFieldValue( "sfSkillsGroup", response.data.data.prediction.skills_groups.filter( ( e ) => softSkillsSet.includes( e ) ) )
				form.setFieldValue( "analysisSource", pdfText ?? url ?? text )
				form.setFieldValue( "skillType", skillType )
				showMessage( { type: "success", content: `${formattedAnalysisType} analysis done.` } )
			} else {
				if ( skillType === "url" && response.data.errorCode === "NO_TEXT" ) {
					showMessage( { type: "error", content: "URL cannot be analyzed due to missing page content!" } )
				} else {
					showMessage( { type: "error", content: "Something went wrong!" } )
				}
			}
		} catch ( _e ) {
			if ( !analyzingRef.current ) {
				return
			}
			showMessage( { type: "loading", content: `Analyzing ${formattedAnalysisType}...`, key: "updatable", duration: 0.2 } )
			showMessage( { type: "error", content: "Something went wrong!" } )
		} finally {
			onAnalyzingEnd()
		}
	}
	const handleMenuClick = ( { key } ) => {
		switch ( key ) {
			case "manual":
				openAddForm()
				return
			case "description":
				setOpenedModal( "description" )
				return
			case "pdf":
				uploadPdfInputRef.current?.click()
				return
			case "url":
				setOpenedModal( "url" )
				return
			case "pick":
				onPickTrainings()
				return
			default:
				return
		}
	}

	return <div>
		<Dropdown menu={ { items: menuItems, onClick: handleMenuClick } }>
			<Button
				className={ "list-container-action-button" }
				icon={ <PlusOutlined className={ "icon-position-fix" } /> }
			>
				{ "Add Skill" }
			</Button>
		</Dropdown>
		<AnalyzeDescriptionModal
			open={ openedModal === "description" }
			onCancel={ () => {
				setOpenedModal( null )
			} }
			onDescriptionSubmit={ ( text ) => void handleAnalyze( "description", { text } ) }
		/>
		<AnalyzeUrlModal
			open={ openedModal === "url" }
			onCancel={ () => {
				setOpenedModal( null )
			} }
			onUrlSubmit={ ( url ) => void handleAnalyze( "url", { url } ) }
		/>
		<input
			type={ "file" }
			accept={ "application/pdf" }
			ref={ uploadPdfInputRef }
			style={ { display: "none" } }
			onChange={ ( e ) => {
				const file = e.target.files[0]
				if ( !file ) {
					return
				}
				void handleAnalyze( "pdf", { file } )
					.finally( () => {
						e.target.value = ""
					} )
			} }
		/>
	</div>
}
